/**
 * RoutePlanningPage - Pagina de Planejamento de Rotas
 * Sistema de roteirizacao profissional Fleet Premium
 *
 * Funcionalidades:
 * - Adicionar pontos de entrega
 * - Selecionar veiculos
 * - Otimizar rotas com VROOM
 * - Visualizar rotas no mapa
 */

import {
  useState, useCallback, useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, shallowEqual } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Chip,
  Tooltip,
  Alert,
  Collapse,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import {
  Add as AddIcon,
  Route as OptimizeIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  LocalShipping as VehicleIcon,
  Place as DeliveryIcon,
  Timeline as RouteIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Download as ExportIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  FolderOpen as LoadIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  PlayCircleOutline as ExecuteIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';

import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useTranslation } from '../common/components/LocalizationProvider';
import MapView from '../map/core/MapView';

import {
  DeliveryPointCard,
  VehicleCard,
  OptimizedRouteCard,
  AddDeliveryModal,
  RoutePlanningMap,
  CostConfigDialog,
  CostSummaryCard,
  CSVImportDialog,
  RouteLegend,
  AssignDriverModal,
} from './components';

import {
  optimizeRoutes,
  optimizeTSP,
  calculateRoute,
  formatDistance,
  formatDuration,
} from '../common/services/routingService';

import routePlanService, {
  PLAN_STATUS,
} from '../common/services/routePlanService';

import {
  exportRoutePlanExcel,
  exportRoutePlanPdf,
} from './services/exportRoutePlan';

import {
  loadCostConfig,
} from './services/costCalculationService';

const ROUTE_COLORS = [
  '#00d4aa', '#f59e0b', '#8b5cf6', '#ef4444',
  '#10b981', '#3b82f6', '#ec4899', '#6366f1',
];

const RoutePlanningPage = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Redux - devices (memoizado para evitar rerenders)
  const devicesItems = useSelector((state) => state.devices.items, shallowEqual);
  const devices = useMemo(() => Object.values(devicesItems), [devicesItems]);
  const positions = useSelector((state) => state.session.positions, shallowEqual);

  // State
  const [deliveries, setDeliveries] = useState([]);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState(new Set());
  const [optimizedRoutes, setOptimizedRoutes] = useState([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(null);

  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationError, setOptimizationError] = useState(null);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [assignDriverOpen, setAssignDriverOpen] = useState(false);
  const [assignDriverTarget, setAssignDriverTarget] = useState(null);

  const [vehiclesExpanded, setVehiclesExpanded] = useState(true);
  const [deliveriesExpanded, setDeliveriesExpanded] = useState(true);
  const [routesExpanded, setRoutesExpanded] = useState(true);

  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleListLimit, setVehicleListLimit] = useState(20);

  // State para persistencia de planos
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [currentPlanName, setCurrentPlanName] = useState('');
  const [savedPlans, setSavedPlans] = useState([]);
  const [loadPlansModalOpen, setLoadPlansModalOpen] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [savePlanName, setSavePlanName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // State para exportacao
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // State para custos
  const [costConfigOpen, setCostConfigOpen] = useState(false);
  const [costConfig, setCostConfig] = useState(loadCostConfig());

  // Veiculos com posicao atual
  const vehiclesWithPosition = useMemo(() => {
    return devices.map((device) => {
      const position = positions[device.id];
      return {
        id: device.id,
        name: device.name,
        uniqueId: device.uniqueId,
        plate: device.attributes?.plate,
        category: device.category,
        status: device.status,
        driver: device.attributes?.driver,
        capacity: device.attributes?.capacity || 1000,
        lat: position?.latitude,
        lng: position?.longitude,
        speed: position?.speed,
      };
    });
  }, [devices, positions]);

  // Veiculos filtrados pela busca
  const filteredVehicles = useMemo(() => {
    if (!vehicleSearch.trim()) return vehiclesWithPosition;
    const search = vehicleSearch.toLowerCase();
    return vehiclesWithPosition.filter((v) =>
      v.name?.toLowerCase().includes(search) ||
      v.uniqueId?.toLowerCase().includes(search) ||
      v.plate?.toLowerCase().includes(search) ||
      v.driver?.toLowerCase().includes(search)
    );
  }, [vehiclesWithPosition, vehicleSearch]);

  // Veiculos selecionados
  const selectedVehicles = useMemo(() => {
    return vehiclesWithPosition.filter((v) => selectedVehicleIds.has(v.id));
  }, [vehiclesWithPosition, selectedVehicleIds]);

  // Stats
  const stats = useMemo(() => {
    const totalDistance = optimizedRoutes.reduce((sum, r) => sum + (r.distance || 0), 0);
    const totalDuration = optimizedRoutes.reduce((sum, r) => sum + (r.duration || 0), 0);

    return {
      deliveries: deliveries.length,
      vehicles: selectedVehicleIds.size,
      routes: optimizedRoutes.length,
      distance: totalDistance,
      duration: totalDuration,
    };
  }, [deliveries, selectedVehicleIds, optimizedRoutes]);

  // Toggle vehicle selection
  const handleVehicleSelect = useCallback((vehicleId) => {
    setSelectedVehicleIds((prev) => {
      const next = new Set(prev);
      if (next.has(vehicleId)) {
        next.delete(vehicleId);
      } else {
        next.add(vehicleId);
      }
      return next;
    });
    // Limpar rotas ao mudar veiculos
    setOptimizedRoutes([]);
  }, []);

  // Selecionar todos os veiculos filtrados
  const handleSelectAllFiltered = useCallback(() => {
    setSelectedVehicleIds((prev) => {
      const next = new Set(prev);
      filteredVehicles.forEach((v) => next.add(v.id));
      return next;
    });
    setOptimizedRoutes([]);
  }, [filteredVehicles]);

  // Limpar selecao dos veiculos filtrados
  const handleClearFilteredSelection = useCallback(() => {
    setSelectedVehicleIds((prev) => {
      const next = new Set(prev);
      filteredVehicles.forEach((v) => next.delete(v.id));
      return next;
    });
    setOptimizedRoutes([]);
  }, [filteredVehicles]);

  // Verificar se todos filtrados estao selecionados
  const allFilteredSelected = useMemo(() => {
    if (filteredVehicles.length === 0) return false;
    return filteredVehicles.every((v) => selectedVehicleIds.has(v.id));
  }, [filteredVehicles, selectedVehicleIds]);

  const someFilteredSelected = useMemo(() => {
    return filteredVehicles.some((v) => selectedVehicleIds.has(v.id));
  }, [filteredVehicles, selectedVehicleIds]);

  // Adicionar/Editar entrega
  const handleAddDelivery = useCallback((delivery) => {
    setDeliveries((prev) => {
      // Verificar se o ID ja existe na lista
      const existingIndex = prev.findIndex((d) => d.id === delivery.id);
      if (existingIndex >= 0) {
        // Editando - substituir existente
        return prev.map((d) => (d.id === delivery.id ? delivery : d));
      }
      // Adicionando nova entrega
      return [...prev, delivery];
    });
    setEditingDelivery(null);
    // Limpar rotas ao modificar entregas
    setOptimizedRoutes([]);
  }, []);

  // Editar entrega
  const handleEditDelivery = useCallback((delivery) => {
    setEditingDelivery(delivery);
    setAddModalOpen(true);
  }, []);

  // Remover entrega
  const handleRemoveDelivery = useCallback((deliveryId) => {
    setDeliveries((prev) => prev.filter((d) => d.id !== deliveryId));
    setOptimizedRoutes([]);
  }, []);

  // Arrastar entrega no mapa (drag & drop)
  const handleDeliveryDrag = useCallback(async (deliveryId, newCoords) => {
    // Atualizar coordenadas imediatamente
    setDeliveries((prev) => prev.map((d) => {
      if (d.id === deliveryId) {
        return {
          ...d,
          lat: newCoords.lat,
          lng: newCoords.lng,
          address: 'Atualizando endereco...',
        };
      }
      return d;
    }));

    // Limpar rotas otimizadas (forcam re-otimizacao)
    setOptimizedRoutes([]);

    // Fazer reverse geocode para novo endereco
    try {
      const { reverseGeocode } = await import('../common/services/routingService');
      const result = await reverseGeocode(newCoords.lat, newCoords.lng);

      setDeliveries((prev) => prev.map((d) => {
        if (d.id === deliveryId) {
          return {
            ...d,
            address: result.displayName || `${newCoords.lat.toFixed(6)}, ${newCoords.lng.toFixed(6)}`,
            name: d.name || result.address?.road || result.address?.suburb || '',
          };
        }
        return d;
      }));

      setSnackbar({
        open: true,
        message: 'Entrega reposicionada com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      console.error('[RoutePlanningPage] Reverse geocode failed:', error);
      // Manter coordenadas mesmo se falhar
      setDeliveries((prev) => prev.map((d) => {
        if (d.id === deliveryId) {
          return {
            ...d,
            address: `${newCoords.lat.toFixed(6)}, ${newCoords.lng.toFixed(6)}`,
          };
        }
        return d;
      }));
    }
  }, []);

  // Importar entregas via CSV
  const handleCSVImport = useCallback((importedDeliveries, mode) => {
    if (mode === 'replace') {
      setDeliveries(importedDeliveries);
    } else {
      // merge - adicionar as existentes
      setDeliveries((prev) => [...prev, ...importedDeliveries]);
    }

    // Limpar rotas otimizadas
    setOptimizedRoutes([]);

    setSnackbar({
      open: true,
      message: `${importedDeliveries.length} entrega(s) importada(s) com sucesso!`,
      severity: 'success',
    });
  }, []);

  // Abrir modal de atribuicao de motorista
  const handleOpenAssignDriver = useCallback(({ planId, planVehicleId, vehicle }) => {
    setAssignDriverTarget({ planId, planVehicleId, vehicle });
    setAssignDriverOpen(true);
  }, []);

  // Quando motorista e atribuido
  const handleDriverAssigned = useCallback(({ driverId: _driverId, driverName }) => {
    // Atualizar o veiculo com o nome do motorista
    setSelectedVehicleIds((prevIds) => {
      // Manter os IDs mas podemos precisar atualizar os dados do veiculo
      return prevIds;
    });

    setSnackbar({
      open: true,
      message: `Motorista ${driverName} atribuido com sucesso!`,
      severity: 'success',
    });

    // Recarregar o plano se tiver um salvo
    if (currentPlanId) {
      // Opcional: recarregar dados do plano
    }
  }, [currentPlanId]);

  // Limpar tudo
  const handleClearAll = useCallback(() => {
    setDeliveries([]);
    setSelectedVehicleIds(new Set());
    setOptimizedRoutes([]);
    setOptimizationError(null);
    setCurrentPlanId(null);
    setCurrentPlanName('');
    setEditingDelivery(null);
  }, []);

  // ============================================================================
  // PERSISTENCIA - Salvar e Carregar Planos
  // ============================================================================

  // Carregar lista de planos salvos
  const loadSavedPlans = useCallback(async () => {
    setIsLoadingPlans(true);
    try {
      const plans = await routePlanService.getRoutePlans();
      setSavedPlans(plans);
    } catch (error) {
      console.error('[RoutePlanningPage] Error loading plans:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar planos: ' + error.message, severity: 'error' });
    } finally {
      setIsLoadingPlans(false);
    }
  }, []);

  // Abrir modal de carregar planos
  const handleOpenLoadModal = useCallback(async () => {
    setLoadPlansModalOpen(true);
    await loadSavedPlans();
  }, [loadSavedPlans]);

  // Salvar plano atual
  const handleSavePlan = useCallback(async () => {
    if (!savePlanName.trim() && !currentPlanId) {
      setSnackbar({ open: true, message: 'Digite um nome para o plano', severity: 'warning' });
      return;
    }

    setIsSaving(true);
    try {
      // Preparar dados do plano
      const planData = {
        plan: {
          id: currentPlanId,
          name: savePlanName.trim() || currentPlanName,
          planDate: new Date().toISOString(),
          status: optimizedRoutes.length > 0 ? PLAN_STATUS.OPTIMIZED : PLAN_STATUS.DRAFT,
          optimizationAlgorithm: optimizedRoutes[0]?.optimizationMethod || 'VROOM',
          totalDistance: stats.distance,
          totalDuration: stats.duration,
          notes: `${stats.deliveries} entregas, ${stats.vehicles} veículos`,
        },
        vehicles: selectedVehicles.map((vehicle, vIndex) => {
          const route = optimizedRoutes.find((r) => r.vehicleId === vehicle.id);
          const vehicleStops = route?.steps?.filter((s) => s.type === 'job') || [];

          return {
            deviceId: vehicle.id,
            driverId: null,
            color: ROUTE_COLORS[vIndex % ROUTE_COLORS.length],
            capacity: vehicle.capacity,
            startLat: vehicle.lat,
            startLng: vehicle.lng,
            routeGeometry: route?.geometry ? JSON.stringify(route.geometry) : null,
            distance: route?.distance || 0,
            duration: route?.duration || 0,
            stops: vehicleStops.map((step) => {
              const delivery = deliveries.find((d) => d.id === step.id);
              return {
                poiId: delivery?.poiId,
                name: step.name || delivery?.name,
                address: step.address || delivery?.address,
                lat: step.location?.lat || delivery?.lat,
                lng: step.location?.lng || delivery?.lng,
                serviceTime: step.serviceTime || delivery?.serviceTime || 300,
                priority: delivery?.priority || 'medium',
                estimatedArrival: step.arrival ? new Date(Date.now() + step.arrival * 1000).toISOString() : null,
                notes: delivery?.notes,
              };
            }),
          };
        }),
      };

      // Adicionar entregas nao alocadas (sem rota)
      const allocatedDeliveryIds = new Set();
      planData.vehicles.forEach((v) => {
        v.stops.forEach((s) => {
          const delivery = deliveries.find((d) => d.name === s.name || d.address === s.address);
          if (delivery) allocatedDeliveryIds.add(delivery.id);
        });
      });

      const unallocatedDeliveries = deliveries.filter((d) => !allocatedDeliveryIds.has(d.id));
      if (unallocatedDeliveries.length > 0 && planData.vehicles.length === 0) {
        // Criar veiculo virtual para entregas nao alocadas
        planData.vehicles.push({
          deviceId: null,
          driverId: null,
          color: '#999999',
          stops: unallocatedDeliveries.map((d) => ({
            poiId: d.poiId,
            name: d.name,
            address: d.address,
            lat: d.lat,
            lng: d.lng,
            serviceTime: d.serviceTime || 300,
            priority: d.priority || 'medium',
          })),
        });
      }

      const result = await routePlanService.saveCompletePlan(planData);

      setCurrentPlanId(result.plan.id);
      setCurrentPlanName(result.plan.name);
      setSaveModalOpen(false);
      setSavePlanName('');
      setSnackbar({ open: true, message: 'Plano salvo com sucesso!', severity: 'success' });
    } catch (error) {
      console.error('[RoutePlanningPage] Error saving plan:', error);
      setSnackbar({ open: true, message: 'Erro ao salvar plano: ' + error.message, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [currentPlanId, currentPlanName, savePlanName, selectedVehicles, deliveries, optimizedRoutes, stats]);

  // Carregar um plano salvo
  const handleLoadPlan = useCallback(async (planId) => {
    setIsLoadingPlans(true);
    try {
      const summary = await routePlanService.loadCompletePlan(planId);

      // Limpar estado atual
      handleClearAll();

      // Restaurar dados do plano
      setCurrentPlanId(summary.plan.id);
      setCurrentPlanName(summary.plan.name);

      // Restaurar veiculos selecionados
      const vehicleIds = new Set();
      (summary.vehicles || []).forEach((v) => {
        if (v.deviceId) vehicleIds.add(v.deviceId);
      });
      setSelectedVehicleIds(vehicleIds);

      // Restaurar entregas
      const loadedDeliveries = [];
      (summary.stops || []).forEach((stop, index) => {
        loadedDeliveries.push({
          id: `loaded-${stop.id || index}`,
          poiId: stop.poiId,
          name: stop.name,
          address: stop.address,
          lat: stop.latitude,
          lng: stop.longitude,
          serviceTime: stop.serviceTime || 300,
          priority: stop.priority || 'medium',
          notes: stop.notes,
        });
      });
      setDeliveries(loadedDeliveries);

      setLoadPlansModalOpen(false);
      setSnackbar({ open: true, message: `Plano "${summary.plan.name}" carregado!`, severity: 'success' });
    } catch (error) {
      console.error('[RoutePlanningPage] Error loading plan:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar plano: ' + error.message, severity: 'error' });
    } finally {
      setIsLoadingPlans(false);
    }
  }, [handleClearAll]);

  // Excluir um plano salvo
  const handleDeletePlan = useCallback(async (planId) => {
    try {
      await routePlanService.deleteRoutePlan(planId);
      setSavedPlans((prev) => prev.filter((p) => p.id !== planId));
      if (currentPlanId === planId) {
        setCurrentPlanId(null);
        setCurrentPlanName('');
      }
      setSnackbar({ open: true, message: 'Plano excluído!', severity: 'success' });
    } catch (error) {
      console.error('[RoutePlanningPage] Error deleting plan:', error);
      setSnackbar({ open: true, message: 'Erro ao excluir plano: ' + error.message, severity: 'error' });
    }
  }, [currentPlanId]);

  // ============================================================================
  // EXPORTACAO - Excel e PDF
  // ============================================================================

  const handleExportExcel = useCallback(async () => {
    setExportMenuAnchor(null);
    setIsExporting(true);
    try {
      await exportRoutePlanExcel({
        planName: currentPlanName || 'Plano de Rota',
        routes: optimizedRoutes,
        vehicles: selectedVehicles,
        deliveryPoints: deliveries,
        summary: {
          totalDistance: stats.distance,
          totalDuration: stats.duration,
          deliveredCount: stats.deliveries,
        },
        theme,
      });
      setSnackbar({ open: true, message: 'Excel exportado com sucesso!', severity: 'success' });
    } catch (error) {
      console.error('[Export] Excel error:', error);
      setSnackbar({ open: true, message: 'Erro ao exportar Excel: ' + error.message, severity: 'error' });
    } finally {
      setIsExporting(false);
    }
  }, [currentPlanName, optimizedRoutes, selectedVehicles, deliveries, stats, theme]);

  const handleExportPdf = useCallback(async () => {
    setExportMenuAnchor(null);
    setIsExporting(true);
    try {
      await exportRoutePlanPdf({
        planName: currentPlanName || 'Plano de Rota',
        routes: optimizedRoutes,
        vehicles: selectedVehicles,
        deliveryPoints: deliveries,
        summary: {
          totalDistance: stats.distance,
          totalDuration: stats.duration,
          deliveredCount: stats.deliveries,
        },
        theme,
        t,
      });
      setSnackbar({ open: true, message: 'PDF exportado com sucesso!', severity: 'success' });
    } catch (error) {
      console.error('[Export] PDF error:', error);
      setSnackbar({ open: true, message: 'Erro ao exportar PDF: ' + error.message, severity: 'error' });
    } finally {
      setIsExporting(false);
    }
  }, [currentPlanName, optimizedRoutes, selectedVehicles, deliveries, stats, theme, t]);

  // Handler para reordenacao manual de paradas
  const handleStopsReorder = useCallback(async (routeIndex, newSteps) => {
    // Atualizar estado local imediatamente para feedback visual
    setOptimizedRoutes((prev) => {
      const updated = [...prev];
      const route = updated[routeIndex];
      if (!route) return prev;

      // Reconstruir steps com nova ordem
      const startStep = route.steps?.find((s) => s.type === 'start');
      const endStep = route.steps?.find((s) => s.type === 'end');

      updated[routeIndex] = {
        ...route,
        steps: [
          startStep,
          ...newSteps.map((step) => ({ ...step, type: 'job' })),
          endStep,
        ].filter(Boolean),
        needsRecalc: true, // Flag para indicar que precisa recalcular
      };

      return updated;
    });

    // Recalcular rota com OSRM (async)
    try {
      const route = optimizedRoutes[routeIndex];
      if (!route) return;

      const vehicle = selectedVehicles.find((v) => v.id === route.vehicleId);
      if (!vehicle?.lat || !vehicle?.lng) return;

      // Preparar coordenadas na nova ordem
      const coordinates = [
        { lat: vehicle.lat, lng: vehicle.lng }, // Start
        ...newSteps.map((s) => ({ lat: s.location?.lat || s.lat, lng: s.location?.lng || s.lng })),
      ];

      // Calcular nova rota
      const routeResult = await calculateRoute(coordinates, 'car', { overview: 'full' });

      // Atualizar com dados da nova rota
      setOptimizedRoutes((prev) => {
        const updated = [...prev];
        if (!updated[routeIndex]) return prev;

        // Atualizar arrival times baseado nos legs
        let accumulatedDuration = 0;
        const updatedSteps = [
          { type: 'start', location: { lat: vehicle.lat, lng: vehicle.lng }, arrival: 0 },
        ];

        newSteps.forEach((step, idx) => {
          const legDuration = routeResult.legs?.[idx]?.duration || 0;
          accumulatedDuration += legDuration;

          updatedSteps.push({
            ...step,
            type: 'job',
            arrival: Math.round(accumulatedDuration),
            optimizedOrder: idx + 1,
          });

          // Adicionar tempo de servico
          accumulatedDuration += (step.serviceTime || 300);
        });

        updatedSteps.push({
          type: 'end',
          location: updatedSteps[updatedSteps.length - 1]?.location,
          arrival: Math.round(accumulatedDuration),
        });

        updated[routeIndex] = {
          ...updated[routeIndex],
          steps: updatedSteps,
          distance: routeResult.distance,
          duration: routeResult.duration,
          geometry: routeResult.geometry,
          needsRecalc: false,
          optimizationMethod: 'Manual Reorder + OSRM',
        };

        return updated;
      });

      setSnackbar({ open: true, message: 'Rota recalculada com sucesso!', severity: 'success' });
    } catch (error) {
      console.error('[RoutePlanningPage] Error recalculating route:', error);
      setSnackbar({ open: true, message: 'Erro ao recalcular rota: ' + error.message, severity: 'warning' });
    }
  }, [optimizedRoutes, selectedVehicles]);

  // ============================================================================
  // OTIMIZACAO - Algoritmos e helpers
  // ============================================================================

  // Calcula distancia haversine entre dois pontos (em metros)
  const haversineDistance = useCallback((lat1, lng1, lat2, lng2) => {
    const R = 6371000; // Raio da Terra em metros
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }, []);

  // Nearest Neighbor TSP - ordena jobs por proximidade
  const optimizeJobOrder = useCallback((jobs, startLat, startLng) => {
    if (jobs.length <= 1) return jobs;

    const remaining = [...jobs];
    const ordered = [];
    let currentLat = startLat;
    let currentLng = startLng;

    while (remaining.length > 0) {
      // Encontrar job mais proximo
      let nearestIdx = 0;
      let nearestDist = Infinity;

      remaining.forEach((job, idx) => {
        const dist = haversineDistance(currentLat, currentLng, job.lat, job.lng);
        // Considerar prioridade: alta = menor distancia virtual
        const priorityFactor = job.priority === 10 ? 0.5 : job.priority === 1 ? 1.5 : 1;
        const effectiveDist = dist * priorityFactor;

        if (effectiveDist < nearestDist) {
          nearestDist = effectiveDist;
          nearestIdx = idx;
        }
      });

      const nearest = remaining.splice(nearestIdx, 1)[0];
      ordered.push(nearest);
      currentLat = nearest.lat;
      currentLng = nearest.lng;
    }

    return ordered;
  }, [haversineDistance]);

  // Fallback: criar rota otimizada com OSRM /trip (TSP nativo)
  const createOptimizedRouteWithOSRM = useCallback(async (vehicle, jobsForVehicle) => {
    const startLat = vehicle.lat;
    const startLng = vehicle.lng;
    const startPoint = { lat: startLat, lng: startLng };

    // Preparar waypoints: ponto inicial + jobs
    const waypoints = [
      { ...startPoint, id: 'start' },
      ...jobsForVehicle.map((j) => ({ lat: j.lat, lng: j.lng, id: j.id })),
    ];

    try {
      // Usar endpoint /trip do OSRM para otimizacao TSP
      const tspResult = await optimizeTSP(waypoints, {
        profile: 'car',
        roundtrip: false, // Nao retornar ao inicio
        source: 'first',  // Primeiro ponto e a origem (veiculo)
        destination: 'last', // Ultimo ponto e o destino
      });

      // Construir steps na ordem otimizada
      let accumulatedDuration = 0;
      const steps = [
        { type: 'start', location: startPoint, arrival: 0 },
      ];

      // Processar waypoints na ordem otimizada (pular o primeiro que e o start)
      const orderedJobs = tspResult.orderedWaypoints
        .filter((wp) => wp.id !== 'start')
        .map((wp) => jobsForVehicle.find((j) => j.id === wp.id));

      orderedJobs.forEach((job, index) => {
        if (!job) return;

        // Usar duracao real das legs do OSRM
        const legDuration = tspResult.legs?.[index]?.duration ||
                           (tspResult.duration / orderedJobs.length);
        accumulatedDuration += legDuration;

        steps.push({
          type: 'job',
          id: job.id,
          location: { lat: job.lat, lng: job.lng },
          name: job.name,
          address: job.address,
          arrival: Math.round(accumulatedDuration),
          serviceTime: job.serviceTime || 300,
        });

        // Adicionar tempo de servico
        accumulatedDuration += (job.serviceTime || 300);
      });

      steps.push({ type: 'end', location: steps[steps.length - 1]?.location || startPoint, arrival: Math.round(accumulatedDuration) });

      return {
        vehicleId: vehicle.id,
        distance: tspResult.distance,
        duration: tspResult.duration,
        geometry: tspResult.geometry,
        steps,
        isFallback: true,
        optimizationMethod: 'OSRM TSP',
      };
    } catch (tspError) {
      console.warn(`[OSRM TSP] Failed, falling back to Nearest Neighbor:`, tspError.message);

      // Fallback do fallback: Nearest Neighbor + calculateRoute
      try {
        const optimizedJobs = optimizeJobOrder(jobsForVehicle, startLat, startLng);
        const coordinates = [startPoint, ...optimizedJobs.map((j) => ({ lat: j.lat, lng: j.lng }))];

        const routeResult = await calculateRoute(coordinates, 'car', { overview: 'full' });

        let accumulatedDuration = 0;
        const steps = [{ type: 'start', location: startPoint, arrival: 0 }];

        optimizedJobs.forEach((job, index) => {
          const legDuration = routeResult.legs?.[index]?.duration || (routeResult.duration / optimizedJobs.length);
          accumulatedDuration += legDuration;

          steps.push({
            type: 'job',
            id: job.id,
            location: { lat: job.lat, lng: job.lng },
            name: job.name,
            address: job.address,
            arrival: Math.round(accumulatedDuration),
            serviceTime: job.serviceTime || 300,
          });
          accumulatedDuration += (job.serviceTime || 300);
        });

        steps.push({ type: 'end', location: startPoint, arrival: Math.round(routeResult.duration) });

        return {
          vehicleId: vehicle.id,
          distance: routeResult.distance,
          duration: routeResult.duration,
          geometry: routeResult.geometry,
          steps,
          isFallback: true,
          optimizationMethod: 'Nearest Neighbor',
        };
      } catch (nnError) {
        console.error(`[OSRM] All fallbacks failed for vehicle ${vehicle.id}:`, nnError);
        return null;
      }
    }
  }, [optimizeJobOrder]);

  const handleOptimize = useCallback(async () => {
    if (deliveries.length === 0 || selectedVehicles.length === 0) {
      return;
    }

    setIsOptimizing(true);
    setOptimizationError(null);

    // ========================================================================
    // VALIDACAO PRE-OTIMIZACAO
    // ========================================================================

    // Verificar veiculos sem posicao
    const vehiclesWithoutPosition = selectedVehicles.filter((v) => !v.lat || !v.lng);
    if (vehiclesWithoutPosition.length > 0) {
      // Usar centroide das entregas como posicao inicial
      const avgLat = deliveries.reduce((sum, d) => sum + d.lat, 0) / deliveries.length;
      const avgLng = deliveries.reduce((sum, d) => sum + d.lng, 0) / deliveries.length;

      console.warn('[Optimize] Vehicles without position, using delivery centroid:', {
        vehicles: vehiclesWithoutPosition.map((v) => v.name),
        centroid: { lat: avgLat, lng: avgLng },
      });

      // Atualizar veiculos sem posicao com centroide
      vehiclesWithoutPosition.forEach((v) => {
        v.lat = avgLat;
        v.lng = avgLng;
      });
    }

    // Calcular peso total das entregas e capacidade total
    const totalWeight = deliveries.reduce((sum, d) => sum + (d.weight || 0), 0);
    const totalCapacity = selectedVehicles.reduce((sum, v) => sum + (v.capacity || 0), 0);
    const hasCapacityConstraints = totalWeight > 0 && totalCapacity > 0;

    // ========================================================================
    // TENTAR VROOM (OTIMIZACAO VRP COMPLETA)
    // ========================================================================

    try {
      // Criar mapeamento de IDs string para indices numericos (VROOM requer IDs inteiros)
      const deliveryIdMap = new Map();
      deliveries.forEach((d, index) => {
        deliveryIdMap.set(index + 1, d.id);
      });

      // Preparar veiculos para VROOM
      const vroomVehicles = selectedVehicles.map((v) => ({
        id: v.id,
        startLat: v.lat,
        startLng: v.lng,
        // So enviar capacity se temos restricoes de peso
        capacity: hasCapacityConstraints ? (v.capacity || 10000) : undefined,
        profile: 'car',
      }));

      // Preparar jobs para VROOM
      const vroomJobs = deliveries.map((d, index) => {
        const job = {
          id: index + 1,
          lat: d.lat,
          lng: d.lng,
          serviceTime: d.serviceTime || 300,
          priority: d.priority === 'high' ? 10 : d.priority === 'low' ? 1 : 5,
        };

        // So adicionar delivery weight se temos restricoes
        if (hasCapacityConstraints && d.weight) {
          job.delivery = d.weight;
        }

        // Adicionar time windows se definidas
        if (d.timeWindow?.start && d.timeWindow?.end) {
          // Converter HH:MM para segundos desde meia-noite
          const parseTime = (t) => {
            const [h, m] = t.split(':').map(Number);
            return h * 3600 + m * 60;
          };
          job.timeWindows = [[parseTime(d.timeWindow.start), parseTime(d.timeWindow.end)]];
        }

        return job;
      });

      const result = await optimizeRoutes({
        vehicles: vroomVehicles,
        jobs: vroomJobs,
      });

      // Enriquecer rotas com informacoes (mapeando IDs de volta)
      const enrichedRoutes = result.routes.map((route) => ({
        ...route,
        steps: route.steps.map((step) => {
          if (step.type === 'job') {
            const originalId = deliveryIdMap.get(step.id);
            const delivery = deliveries.find((d) => d.id === originalId);
            return {
              ...step,
              id: originalId,
              name: delivery?.name,
              address: delivery?.address,
            };
          }
          return step;
        }),
      }));

      setOptimizedRoutes(enrichedRoutes);

      // Atualizar cores das entregas
      setDeliveries((prev) => prev.map((d) => {
        const routeIndex = enrichedRoutes.findIndex((r) => r.steps.some((s) => s.id === d.id));
        return {
          ...d,
          routeColor: routeIndex >= 0 ? ROUTE_COLORS[routeIndex % ROUTE_COLORS.length] : undefined,
        };
      }));

      // Alertas
      const warnings = [];
      if (result.unassigned?.length > 0) {
        const unassignedNames = result.unassigned.map((u) => {
          const originalId = deliveryIdMap.get(u.id);
          const delivery = deliveries.find((d) => d.id === originalId);
          return delivery?.name || `Entrega ${u.id}`;
        });
        warnings.push(`${result.unassigned.length} entrega(s) nao alocadas: ${unassignedNames.join(', ')}`);
      }
      if (warnings.length > 0) {
        setOptimizationError(warnings.join('. '));
      }

    } catch (vroomError) {
      console.warn('[VROOM] Optimization failed:', vroomError.message);

      // ======================================================================
      // FALLBACK: OSRM COM TSP (NEAREST NEIGHBOR)
      // ======================================================================

      try {

        // Distribuir entregas entre veiculos por proximidade geografica
        const vehicleJobs = selectedVehicles.map(() => []);

        // Preparar entregas com prioridade
        const preparedDeliveries = deliveries.map((d) => ({
          id: d.id,
          lat: d.lat,
          lng: d.lng,
          name: d.name,
          address: d.address,
          serviceTime: d.serviceTime || 300,
          priority: d.priority === 'high' ? 10 : d.priority === 'low' ? 1 : 5,
          weight: d.weight || 0,
        }));

        if (selectedVehicles.length === 1) {
          // Um veiculo: todas as entregas
          vehicleJobs[0] = preparedDeliveries;
        } else {
          // Multiplos veiculos: distribuir por proximidade ao veiculo
          preparedDeliveries.forEach((delivery) => {
            // Encontrar veiculo mais proximo
            let nearestVehicleIdx = 0;
            let nearestDist = Infinity;

            selectedVehicles.forEach((vehicle, idx) => {
              const dist = haversineDistance(delivery.lat, delivery.lng, vehicle.lat, vehicle.lng);
              // Balancear carga: penalizar veiculos com mais entregas
              const loadFactor = 1 + (vehicleJobs[idx].length * 0.1);
              if (dist * loadFactor < nearestDist) {
                nearestDist = dist * loadFactor;
                nearestVehicleIdx = idx;
              }
            });

            vehicleJobs[nearestVehicleIdx].push(delivery);
          });
        }

        // Criar rotas otimizadas com TSP
        const routePromises = selectedVehicles.map((vehicle, index) => {
          if (vehicleJobs[index].length === 0) return null;
          return createOptimizedRouteWithOSRM(vehicle, vehicleJobs[index]);
        });

        const routes = (await Promise.all(routePromises)).filter(Boolean);

        if (routes.length > 0) {
          setOptimizedRoutes(routes);
          setDeliveries((prev) => prev.map((d) => {
            const routeIndex = routes.findIndex((r) => r.steps.some((s) => s.id === d.id));
            return {
              ...d,
              routeColor: routeIndex >= 0 ? ROUTE_COLORS[routeIndex % ROUTE_COLORS.length] : undefined,
            };
          }));
          // Verificar qual metodo foi usado
          const method = routes[0]?.optimizationMethod || 'OSRM TSP';
          setOptimizationError(`Usando ${method} (VROOM indisponivel). Rotas otimizadas com sucesso.`);
        } else {
          throw new Error('Nenhuma rota criada');
        }
      } catch (fallbackError) {
        console.error('[Fallback] Failed:', fallbackError);
        setOptimizationError('Falha na otimizacao. Verifique sua conexao.');
      }
    } finally {
      setIsOptimizing(false);
    }
  }, [deliveries, selectedVehicles, haversineDistance, createOptimizedRouteWithOSRM]);

  // Click no mapa para adicionar entrega (com reverse geocoding)
  const handleMapClick = useCallback(async (coords) => {
    // Abrir modal imediatamente com coordenadas
    setEditingDelivery({
      lat: coords.lat,
      lng: coords.lng,
      address: 'Buscando endereço...',
    });
    setAddModalOpen(true);

    // Buscar endereço via reverse geocoding
    try {
      const { reverseGeocode } = await import('../common/services/routingService');
      const result = await reverseGeocode(coords.lat, coords.lng);

      // Atualizar com o endereço encontrado
      setEditingDelivery((prev) => ({
        ...prev,
        address: result.displayName || '',
        name: result.address?.road || result.address?.suburb || '',
      }));
    } catch (error) {
      console.error('[RoutePlanningPage] Reverse geocode failed:', error);
      // Manter coordenadas mesmo se falhar
      setEditingDelivery((prev) => ({
        ...prev,
        address: `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`,
      }));
    }
  }, []);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          overflow: 'hidden',
          backgroundColor: tokens?.surface?.base,
        }}
      >
      {/* Sidebar */}
      <Box
        sx={{
          width: isMobile ? '100%' : 380,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: tokens?.surface?.base,
          borderRight: `1px solid ${tokens?.border?.subtle}`,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${tokens?.border?.subtle}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Voltar">
                <IconButton
                  size="small"
                  onClick={() => navigate(-1)}
                  sx={{
                    color: tokens?.text?.secondary,
                    backgroundColor: tokens?.surface?.elevated,
                    '&:hover': {
                      backgroundColor: tokens?.surface?.hover,
                    },
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography
                sx={{
                  color: tokens?.text?.primary,
                  fontWeight: 600,
                  fontSize: '18px',
                }}
              >
                Planejamento de Rotas
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Carregar Plano">
                <IconButton
                  size="small"
                  onClick={handleOpenLoadModal}
                  sx={{ color: tokens?.text?.tertiary }}
                >
                  <LoadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={currentPlanId ? 'Salvar Plano' : 'Salvar Como'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (currentPlanId) {
                        handleSavePlan();
                      } else {
                        setSaveModalOpen(true);
                      }
                    }}
                    disabled={deliveries.length === 0 && selectedVehicleIds.size === 0}
                    sx={{ color: tokens?.accent?.primary }}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Exportar">
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                    disabled={optimizedRoutes.length === 0 || isExporting}
                    sx={{ color: tokens?.text?.tertiary }}
                  >
                    <ExportIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Limpar tudo">
                <IconButton
                  size="small"
                  onClick={handleClearAll}
                  sx={{ color: tokens?.text?.tertiary }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Nome do plano atual */}
          {currentPlanName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography sx={{ color: tokens?.text?.secondary, fontSize: '12px' }}>
                Plano:
              </Typography>
              <Chip
                label={currentPlanName}
                size="small"
                onDelete={() => {
                  setCurrentPlanId(null);
                  setCurrentPlanName('');
                }}
                sx={{
                  backgroundColor: tokens?.surface?.elevated,
                  color: tokens?.text?.primary,
                  fontSize: '11px',
                }}
              />
            </Box>
          )}

            {/* Stats */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                icon={<DeliveryIcon sx={{ fontSize: '14px !important' }} />}
                label={`${stats.deliveries} entregas`}
                size="small"
                sx={{
                  backgroundColor: `${tokens?.accent?.primary}20`,
                  color: tokens?.accent?.primary,
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
              <Chip
                icon={<VehicleIcon sx={{ fontSize: '14px !important' }} />}
                label={`${stats.vehicles} veiculos`}
                size="small"
                sx={{
                  backgroundColor: `${tokens?.accent?.secondary}20`,
                  color: tokens?.accent?.secondary,
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
              {stats.routes > 0 && (
                <Chip
                  icon={<RouteIcon sx={{ fontSize: '14px !important' }} />}
                  label={formatDistance(stats.distance)}
                  size="small"
                  sx={{
                    backgroundColor: `${tokens?.semantic?.success}20`,
                    color: tokens?.semantic?.success,
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Scrollable content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {/* Erro de otimizacao */}
            <Collapse in={!!optimizationError}>
              <Alert
                severity="warning"
                onClose={() => setOptimizationError(null)}
                sx={{ mb: 2 }}
              >
                {optimizationError}
              </Alert>
            </Collapse>

            {/* Secao: Veiculos */}
            <Box sx={{ mb: 3 }}>
              <Box
                onClick={() => setVehiclesExpanded(!vehiclesExpanded)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VehicleIcon sx={{ color: tokens?.text?.secondary, fontSize: 20 }} />
                  <Typography
                    component="div"
                    sx={{
                      color: tokens?.text?.primary,
                      fontWeight: 600,
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    Veiculos ({vehiclesWithPosition.length})
                    {selectedVehicleIds.size > 0 && (
                      <Chip
                        label={`${selectedVehicleIds.size} selecionados`}
                        size="small"
                        sx={{
                          ml: 1,
                          height: 18,
                          fontSize: '10px',
                          backgroundColor: `${tokens?.accent?.primary}20`,
                          color: tokens?.accent?.primary,
                        }}
                      />
                    )}
                  </Typography>
                </Box>
                {vehiclesExpanded ? <CollapseIcon /> : <ExpandIcon />}
              </Box>

              <Collapse in={vehiclesExpanded}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {/* Campo de busca */}
                  <TextField
                    size="small"
                    placeholder="Buscar veiculo..."
                    value={vehicleSearch}
                    onChange={(e) => {
                      setVehicleSearch(e.target.value);
                      setVehicleListLimit(20); // Reset limit ao buscar
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: tokens?.text?.tertiary, fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: vehicleSearch && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setVehicleSearch('')}
                          >
                            <ClearIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: tokens?.surface?.elevated,
                        borderRadius: tokens?.radius?.md,
                        fontSize: '13px',
                      },
                    }}
                  />

                  {/* Acoes em lote */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      size="small"
                      checked={allFilteredSelected}
                      indeterminate={someFilteredSelected && !allFilteredSelected}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleSelectAllFiltered();
                        } else {
                          handleClearFilteredSelection();
                        }
                      }}
                      sx={{
                        p: 0.5,
                        color: tokens?.text?.tertiary,
                        '&.Mui-checked': { color: tokens?.accent?.primary },
                      }}
                    />
                    <Typography sx={{ color: tokens?.text?.secondary, fontSize: '12px', flex: 1 }}>
                      {vehicleSearch
                        ? `Selecionar ${filteredVehicles.length} filtrados`
                        : 'Selecionar todos'}
                    </Typography>
                    {selectedVehicleIds.size > 0 && (
                      <Button
                        size="small"
                        onClick={() => setSelectedVehicleIds(new Set())}
                        sx={{
                          fontSize: '11px',
                          color: tokens?.text?.tertiary,
                          minWidth: 'auto',
                          p: '2px 8px',
                        }}
                      >
                        Limpar
                      </Button>
                    )}
                  </Box>

                  {/* Lista de veiculos */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {filteredVehicles.length === 0 ? (
                      <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '13px', textAlign: 'center', py: 2 }}>
                        Nenhum veiculo encontrado
                      </Typography>
                    ) : (
                      <>
                        {filteredVehicles.slice(0, vehicleListLimit).map((vehicle) => (
                          <VehicleCard
                            key={vehicle.id}
                            vehicle={vehicle}
                            selected={selectedVehicleIds.has(vehicle.id)}
                            onSelect={handleVehicleSelect}
                            showDetails={false}
                          />
                        ))}

                        {filteredVehicles.length > vehicleListLimit && (
                          <Button
                            size="small"
                            onClick={() => setVehicleListLimit((prev) => prev + 30)}
                            sx={{
                              color: tokens?.accent?.primary,
                              fontSize: '12px',
                              py: 1,
                            }}
                          >
                            Carregar mais ({filteredVehicles.length - vehicleListLimit} restantes)
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              </Collapse>
            </Box>

            <Divider sx={{ borderColor: tokens?.border?.subtle, my: 2 }} />

            {/* Secao: Entregas */}
            <Box sx={{ mb: 3 }}>
              <Box
                onClick={() => setDeliveriesExpanded(!deliveriesExpanded)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DeliveryIcon sx={{ color: tokens?.text?.secondary, fontSize: 20 }} />
                  <Typography
                    sx={{
                      color: tokens?.text?.primary,
                      fontWeight: 600,
                      fontSize: '14px',
                    }}
                  >
                    Entregas ({deliveries.length})
                  </Typography>
                </Box>
                {deliveriesExpanded ? <CollapseIcon /> : <ExpandIcon />}
              </Box>

              <Collapse in={deliveriesExpanded}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {deliveries.length === 0 ? (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: tokens?.surface?.raised,
                        borderRadius: tokens?.radius?.md,
                        border: `1px dashed ${tokens?.border?.default}`,
                      }}
                    >
                      <DeliveryIcon sx={{ color: tokens?.text?.tertiary, fontSize: 32, mb: 1 }} />
                      <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px' }}>
                        Clique no mapa ou use o botao abaixo para adicionar entregas
                      </Typography>
                    </Box>
                  ) : (
                    deliveries.map((delivery, index) => (
                      <DeliveryPointCard
                        key={delivery.id}
                        delivery={delivery}
                        index={index}
                        onEdit={handleEditDelivery}
                        onRemove={handleRemoveDelivery}
                      />
                    ))
                  )}

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setEditingDelivery(null);
                        setAddModalOpen(true);
                      }}
                      sx={{
                        flex: 1,
                        borderColor: tokens?.border?.default,
                        color: tokens?.text?.secondary,
                        borderStyle: 'dashed',
                        '&:hover': {
                          borderColor: tokens?.accent?.primary,
                          color: tokens?.accent?.primary,
                          backgroundColor: `${tokens?.accent?.primary}08`,
                        },
                      }}
                    >
                      Adicionar
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={() => setCsvImportOpen(true)}
                      sx={{
                        borderColor: tokens?.border?.default,
                        color: tokens?.text?.secondary,
                        '&:hover': {
                          borderColor: tokens?.accent?.secondary,
                          color: tokens?.accent?.secondary,
                          backgroundColor: `${tokens?.accent?.secondary}08`,
                        },
                      }}
                    >
                      Importar CSV
                    </Button>
                  </Box>
                </Box>
              </Collapse>
            </Box>

            {/* Secao: Rotas Otimizadas */}
            {optimizedRoutes.length > 0 && (
              <>
                <Divider sx={{ borderColor: tokens?.border?.subtle, my: 2 }} />

                <Box>
                  <Box
                    onClick={() => setRoutesExpanded(!routesExpanded)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      mb: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RouteIcon sx={{ color: tokens?.semantic?.success, fontSize: 20 }} />
                      <Typography
                        sx={{
                          color: tokens?.text?.primary,
                          fontWeight: 600,
                          fontSize: '14px',
                        }}
                      >
                        Rotas Otimizadas ({optimizedRoutes.length})
                      </Typography>
                    </Box>
                    {routesExpanded ? <CollapseIcon /> : <ExpandIcon />}
                  </Box>

                  <Collapse in={routesExpanded}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {optimizedRoutes.map((route, routeIdx) => {
                        const vehicle = selectedVehicles.find((v) => v.id === route.vehicleId);
                        return (
                          <OptimizedRouteCard
                            key={route.vehicleId}
                            route={route}
                            vehicle={vehicle}
                            index={routeIdx}
                            expanded={selectedRouteIndex === routeIdx}
                            onExpandChange={(expanded) => setSelectedRouteIndex(expanded ? routeIdx : null)}
                            onOrderChange={(newSteps) => handleStopsReorder(routeIdx, newSteps)}
                            allowReorder
                            planId={currentPlanId}
                            planVehicleId={route.planVehicleId}
                            onAssignDriver={currentPlanId ? handleOpenAssignDriver : null}
                          />
                        );
                      })}
                    </Box>
                  </Collapse>
                </Box>

                {/* Card de Custos */}
                <Box sx={{ mt: 2 }}>
                  <CostSummaryCard
                    routes={optimizedRoutes}
                    costConfig={costConfig}
                    onConfigClick={() => setCostConfigOpen(true)}
                    compact={false}
                    title="Custos Estimados"
                  />
                </Box>
              </>
            )}
          </Box>

          {/* Footer - Botao Otimizar */}
          <Box
            sx={{
              p: 2,
              borderTop: `1px solid ${tokens?.border?.subtle}`,
              backgroundColor: tokens?.surface?.raised,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={isOptimizing ? <CircularProgress size={20} color="inherit" /> : <OptimizeIcon />}
                onClick={handleOptimize}
                disabled={isOptimizing || deliveries.length === 0 || selectedVehicleIds.size === 0}
                sx={{
                  py: 1.5,
                  backgroundColor: tokens?.accent?.primary,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: tokens?.accent?.hover,
                  },
                  '&:disabled': {
                    backgroundColor: tokens?.surface?.elevated,
                    color: tokens?.text?.tertiary,
                  },
                }}
              >
                {isOptimizing ? 'Otimizando...' : 'Otimizar Rotas'}
              </Button>

              {currentPlanId && optimizedRoutes.length > 0 && (
                <Tooltip title="Iniciar execucao do plano">
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate(`/settings/route-execution/${currentPlanId}`)}
                    sx={{
                      py: 1.5,
                      minWidth: 56,
                      backgroundColor: tokens?.semantic?.success,
                      '&:hover': {
                        backgroundColor: '#16a34a',
                      },
                    }}
                  >
                    <ExecuteIcon />
                  </Button>
                </Tooltip>
              )}
            </Box>

            {deliveries.length > 0 && selectedVehicleIds.size === 0 && (
              <Typography
                sx={{
                  color: tokens?.semantic?.warning,
                  fontSize: '12px',
                  textAlign: 'center',
                  mt: 1,
                }}
              >
                Selecione pelo menos um veiculo
              </Typography>
            )}
          </Box>
        </Box>

        {/* Mapa */}
        {!isMobile && (
          <Box sx={{ flex: 1, position: 'relative' }}>
            <MapView>
              <RoutePlanningMap
                deliveries={deliveries}
                vehicles={selectedVehicles}
                optimizedRoutes={optimizedRoutes}
                selectedRouteIndex={selectedRouteIndex}
                onDeliveryClick={handleEditDelivery}
                onDeliveryDrag={handleDeliveryDrag}
                onMapClick={handleMapClick}
              />
            </MapView>

            {/* Resumo */}
            {optimizedRoutes.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 24,
                  right: 24,
                  backgroundColor: tokens?.surface?.glass,
                  backdropFilter: 'blur(12px)',
                  borderRadius: tokens?.radius?.lg,
                  border: `1px solid ${tokens?.border?.subtle}`,
                  p: 2,
                }}
              >
                <Typography sx={{ color: tokens?.text?.secondary, fontSize: '11px', mb: 1 }}>
                  RESUMO
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography sx={{ color: tokens?.text?.primary, fontSize: '13px' }}>
                    {stats.deliveries} entregas
                  </Typography>
                  <Typography sx={{ color: tokens?.text?.primary, fontSize: '13px' }}>
                    {formatDistance(stats.distance)}
                  </Typography>
                  <Typography sx={{ color: tokens?.text?.primary, fontSize: '13px' }}>
                    {formatDuration(stats.duration)}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Legenda de Segmentos */}
            {optimizedRoutes.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 24,
                  left: 400,
                }}
              >
                <RouteLegend
                  routes={optimizedRoutes}
                  vehicles={selectedVehicles}
                  selectedRouteIndex={selectedRouteIndex}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Modal Adicionar Entrega */}
      <AddDeliveryModal
        open={addModalOpen}
        onClose={() => {
          setAddModalOpen(false);
          setEditingDelivery(null);
        }}
        onSave={handleAddDelivery}
        initialData={editingDelivery}
      />

      {/* Modal Importar CSV */}
      <CSVImportDialog
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
        onImport={handleCSVImport}
        existingDeliveriesCount={deliveries.length}
      />

      {/* Modal Atribuir Motorista */}
      <AssignDriverModal
        open={assignDriverOpen}
        onClose={() => {
          setAssignDriverOpen(false);
          setAssignDriverTarget(null);
        }}
        onAssign={handleDriverAssigned}
        planId={assignDriverTarget?.planId}
        planVehicleId={assignDriverTarget?.planVehicleId}
        vehicleName={assignDriverTarget?.vehicle?.name}
      />

      {/* Modal Salvar Plano */}
      <Dialog
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: tokens?.surface?.base,
            borderRadius: tokens?.radius?.lg,
          },
        }}
      >
        <DialogTitle sx={{ color: tokens?.text?.primary }}>
          Salvar Plano de Rota
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nome do Plano"
            value={savePlanName}
            onChange={(e) => setSavePlanName(e.target.value)}
            placeholder="Ex: Entregas Segunda-feira"
            sx={{ mt: 1 }}
          />
          <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px', mt: 2 }}>
            {stats.deliveries} entregas, {stats.vehicles} veículos
            {stats.distance > 0 && ` • ${formatDistance(stats.distance)}`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSaveModalOpen(false)} sx={{ color: tokens?.text?.secondary }}>
            Cancelar
          </Button>
          <Button
            onClick={handleSavePlan}
            variant="contained"
            disabled={isSaving || !savePlanName.trim()}
            startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{
              backgroundColor: tokens?.accent?.primary,
              '&:hover': { backgroundColor: tokens?.accent?.hover },
            }}
          >
            {isSaving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Carregar Planos */}
      <Dialog
        open={loadPlansModalOpen}
        onClose={() => setLoadPlansModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: tokens?.surface?.base,
            borderRadius: tokens?.radius?.lg,
            maxHeight: '70vh',
          },
        }}
      >
        <DialogTitle sx={{ color: tokens?.text?.primary, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Carregar Plano de Rota
          <IconButton size="small" onClick={loadSavedPlans} disabled={isLoadingPlans}>
            <RefreshIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingPlans ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : savedPlans.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: tokens?.text?.secondary }}>
                Nenhum plano salvo encontrado
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {savedPlans.map((plan) => (
                <ListItem
                  key={plan.id}
                  disablePadding
                  sx={{ mb: 1 }}
                  secondaryAction={
                    <Tooltip title="Excluir">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePlan(plan.id);
                        }}
                        sx={{ color: tokens?.semantic?.error }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemButton
                    onClick={() => handleLoadPlan(plan.id)}
                    sx={{
                      borderRadius: tokens?.radius?.md,
                      backgroundColor: currentPlanId === plan.id ? `${tokens?.accent?.primary}15` : 'transparent',
                      '&:hover': { backgroundColor: tokens?.surface?.hover },
                    }}
                  >
                    <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: tokens?.text?.primary, fontWeight: 500 }}>
                          {plan.name}
                        </Typography>
                        <Chip
                          label={plan.status}
                          size="small"
                          sx={{
                            fontSize: '10px',
                            height: 18,
                            backgroundColor: plan.status === 'optimized' ? `${tokens?.semantic?.success}20` : `${tokens?.text?.tertiary}20`,
                            color: plan.status === 'optimized' ? tokens?.semantic?.success : tokens?.text?.tertiary,
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
                        {plan.planDate ? new Date(plan.planDate).toLocaleDateString('pt-BR') : '-'}
                        {plan.totalDistance > 0 && ` • ${formatDistance(plan.totalDistance)}`}
                        {plan.notes && ` • ${plan.notes}`}
                      </Typography>
                    }
                  />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setLoadPlansModalOpen(false)} sx={{ color: tokens?.text?.secondary }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Configuracao de Custos */}
      <CostConfigDialog
        open={costConfigOpen}
        onClose={() => setCostConfigOpen(false)}
        onSave={(newConfig) => {
          setCostConfig(newConfig);
          setSnackbar({ open: true, message: 'Configuracao de custos salva!', severity: 'success' });
        }}
        initialConfig={costConfig}
      />

      {/* Snackbar de feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Menu de exportacao */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            backgroundColor: tokens?.surface?.raised,
            borderRadius: tokens?.radius?.md,
            border: `1px solid ${tokens?.border?.subtle}`,
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={handleExportExcel}
          disabled={isExporting}
          sx={{
            color: tokens?.text?.primary,
            fontSize: '14px',
            '&:hover': { backgroundColor: tokens?.surface?.hover },
          }}
        >
          <ListItemIcon>
            <ExcelIcon sx={{ color: '#217346', fontSize: 20 }} />
          </ListItemIcon>
          Exportar Excel
        </MenuItem>
        <MenuItem
          onClick={handleExportPdf}
          disabled={isExporting}
          sx={{
            color: tokens?.text?.primary,
            fontSize: '14px',
            '&:hover': { backgroundColor: tokens?.surface?.hover },
          }}
        >
          <ListItemIcon>
            <PdfIcon sx={{ color: '#d32f2f', fontSize: 20 }} />
          </ListItemIcon>
          Exportar PDF
        </MenuItem>
      </Menu>
    </>
  );
};

export default RoutePlanningPage;
